import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import imageCompression from 'browser-image-compression';
import ReactModal from 'react-modal';

function FamilySettingsEditTotem() {
  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const loggedFamilyAdmin = localStorage.getItem('family_admin');
  const [totemModalOpen, setTotemModalOpen] = useState(false);
  const [selectedTotemId, setSelectedTotemId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false); // NEW


  // Fetch totems + family data
  const {
    data: totemData,
    isLoading: isTotemLoading,
    isError: isTotemError,
  } = useQuery({
    queryKey: ['totemSettings'],
    queryFn: () =>
      axios.get(`${API_URL}/totem-settings-api`, { withCredentials: true })
        .then(res => res.data),
    staleTime: 0,
    cacheTime: Infinity,
  });

  const familyData = totemData?.family_data || {};
  const totemList = totemData?.totems || [];

  // Preselect totem whenever data loads
  useEffect(() => {
    if (familyData?.totem_id) {
      setSelectedTotemId(familyData.totem_id);
    }
  }, [familyData]);

  const openModalWithDataTotem = () => {
    setSelectedTotemId(familyData?.totem_id || (totemList[0]?.id ?? null));
    setTotemModalOpen(true);
  };

  const handleUpdateTotem = () => {
    if (!selectedTotemId || isNaN(selectedTotemId)) {
      toast.error('Invalid totem selected.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    setIsUpdating(true); // disable button
    axios.put(`${API_URL}/update-totem-settings-api`, { totem_id: selectedTotemId }, { withCredentials: true })
      .then(() => {
        toast.success('Totem updated successfully', { position: 'top-right', autoClose: 3000 });
        setTotemModalOpen(false);
        queryClient.invalidateQueries(['totemSettings']);
        navigate('/family-settings');
      })
      .catch(err => {
        console.error('Totem update failed:', err.response?.data || err.message);
        toast.error('Update failed. Please try again.', { position: 'top-right', autoClose: 3000 });
      })
      .finally(() => {
        setIsUpdating(false); // re-enable button
      });
  };



  const [editTotemImageModal, setEditTotemImageModalOpen] = useState(false);
const [totemImageFile, setTotemImageFile] = useState(null);
const [previewImage, setPreviewImage] = useState(null);


const handleImageChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  try {
    const options = {
      maxSizeMB: 1,           // ~1MB
      maxWidthOrHeight: 800,  // Resize
      useWebWorker: true,
      initialQuality: 0.8,
    };

    const compressedFile = await imageCompression(file, options);

    // Save compressed file (this is what you upload)
    setTotemImageFile(compressedFile);

    // Preview compressed image
    const previewUrl = URL.createObjectURL(compressedFile);
    setPreviewImage(previewUrl);

  } catch (error) {
    console.error('Image compression error:', error);
    toast.error('Failed to compress image');
  }
};

 


const handleUpdateTotemImage = async () => {
  if (!totemImageFile) {
    toast.error('Select an image first', {
      position: 'top-right',
      autoClose: 3000,
    });
    return;
  }

  setIsUpdating(true);

  try {
    //Further compression before upload (~100KB)
    const options = {
      maxSizeMB: 0.1,           // ≈ 100 KB
      maxWidthOrHeight: 800,
      useWebWorker: true,
      initialQuality: 0.7,
    };

    const compressedImage = await imageCompression(
      totemImageFile,
      options
    );

    const formData = new FormData();
    formData.append(
      'totem_image',
      compressedImage,
      compressedImage.name || 'totem.webp'
    );

    await axios.put(
      `${API_URL}/update-totem-image-settings-api`,
      formData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    toast.success('Totem updated successfully', {
      position: 'top-right',
      autoClose: 3000,
    });

    queryClient.invalidateQueries(['totemSettings']);
    setEditTotemImageModalOpen(false);
    setPreviewImage(null);
    setTotemImageFile(null);

  } catch (err) {
    console.error('Totem image update failed:', err);
    toast.error('Update failed. Please try again.', {
      position: 'top-right',
      autoClose: 3000,
    });
  } finally {
    setIsUpdating(false);
  }
};



const handleRemoveTotemImage = async () => {
  setIsUpdating(true);

  try {
    await axios.put(
      `${API_URL}/remove-totem-image-settings-api`,
      {},
      { withCredentials: true }
    );

    toast.success('Totem image removed successfully', {
      position: 'top-right',
      autoClose: 3000,
    });

    queryClient.invalidateQueries(['totemSettings']);
    setEditTotemImageModalOpen(false);
    setPreviewImage(null);
    setTotemImageFile(null);

  } catch (err) {
    console.error('Totem image removal failed:', err);
    toast.error('Remove failed. Please try again.', {
      position: 'top-right',
      autoClose: 3000,
    });
  } finally {
    setIsUpdating(false);
  }
};




  if (isTotemLoading) {
    return (
      <div className='loadingspinnerDiv'>
        <div className='loadingspinner'></div>
      </div>
    );
  }


  if (isTotemError) return <p>Error loading totems.</p>;

  return (
    <div className='scrollPageContainer theme-light'>
      <div className='main-wrapper'>
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />
        <ToastContainer />

        <div className='page-wrapper'>
          <div className='content container-fluid'>
            
            <div className='row'>
              <div className='col-3 col-md-3'>
                <Link to='/family-settings' style={{ textDecoration: 'none', color: '#184A2C' }}>
                  <i className='fa fa-arrow-left' style={{ fontSize: '18px' }}></i>
                </Link>
              </div>
            </div>

            <div className='row'>
              <div className='col-12 col-md-12'>
                <div className='card'>
                  <div className='card-body'>

                    <p style={{ fontWeight: '600', textAlign: 'center' }}>Edit Totem</p>

                    {familyData && (
                      <div className='mt-4 border rounded p-3'>
                        <h4>
                          
                          {totemList.find(t => t.id === familyData?.totem_id)?.totem_name || 'Update Totem'}
                          
                          
                        </h4>

                       

<div
  style={{
    position: 'relative',
    width: '100%',
    height: '300px',
  }}
>
  <img
    src={`${API_URL}/images/${familyData?.totem_image}`}
    alt="Totem Image"
    className="totem-image-mock"
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '6px',
    }}
  />

  
</div>




                          <div className="position-absolute top-0 end-0 m-2" >
                                {loggedFamilyAdmin === '1' && (
                                    <div className="dropdown"> 

                                <button
                                  className="btn btn-lightx btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0"
                                  
                                  data-bs-toggle="dropdown" aria-expanded="false" aria-label="Open actions"
                                  style={{ width: 28, height: 28 , backgroundColor: '#184A2C', color: '#ffffff'}}
                                >
                                  ⋮
                                </button>
                                
            
                                  <ul className="dropdown-menu dropdown-menu-end p-1"  >
                                   
                                    <li>
                                      <button className="dropdown-item text-success d-flex align-items-center gap-2 py-1 px-2"
                                              onClick={openModalWithDataTotem} >
                                                
                                          Edit Totem
                                      </button>
                                    </li>
                                    


                                      
                                    <li>
                                      <button className="dropdown-item text-success d-flex align-items-center gap-2 py-1 px-2"
                                              onClick={() => setEditTotemImageModalOpen(true)} >
                                                
                                          Totem Image
                                      </button>
                                    </li>
                                    

                                  </ul>
                                            
            
                                          </div>
                                     
                                )}
                                        </div>




                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal */}
              <ReactModal isOpen={totemModalOpen} onRequestClose={() => setTotemModalOpen(false)}
                className='Modal' overlayClassName='Overlay'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Edit Totem</h5>
                  <button type='button' className='btn-close modalCloseButton' onClick={() => setTotemModalOpen(false)}></button>
                </div>

                <div className='modal-body'>
                  <label>Totem Name</label>
                  <select
  className="form-select"
  value={selectedTotemId || ''}
  //onChange={(e) => setSelectedTotemId(parseInt(e.target.value))}

  onChange={(e) => {
    const value = e.target.value;
    if (value === "insert_totem") {
      navigate("/family-settings-edit-totem-add");
    } else {
      setSelectedTotemId(parseInt(value));
    }
  }}
>
  <option value="" >Select a totem</option>
  {totemList.map(totem => (
    <option key={totem.id} value={totem.id}>
      {totem.totem_name}
    </option>
  ))}
  <option value="insert_totem" style={{ fontWeight: "900" }}>
        Insert your totem
      </option>
</select>

                </div>

                <div className='modal-footer'>
                  <button
                    className='btn btn-primary'
                    onClick={handleUpdateTotem}
                    disabled={isUpdating} // Disable during update
                  >
                    {isUpdating ? 'Updating...' : 'Update Totem'}
                  </button>
                </div>
              </ReactModal>



              <ReactModal
  isOpen={editTotemImageModal}
  onRequestClose={() => setEditTotemImageModalOpen(false)}
  className="Modal"
  overlayClassName="Overlay"
>
  <div className="modal-header">
    <h5 className="modal-title">Edit Totem Image</h5>
    <button
      type="button"
      className="btn-close modalCloseButton"
      onClick={() => setEditTotemImageModalOpen(false)}
    />
  </div>

  <div className="modal-body">

    <center>
    <label htmlFor="imageUploadEdit" className="btn btn-outline-success d-inline-flex align-items-center gap-2">
                    <i className="bi bi-upload" /> Select Totem Image
                  </label>
    </center>

<input
  id="imageUploadEdit"
  type="file"
  className="form-control"
  accept="image/*"
  onChange={handleImageChange}
/>

    {previewImage && (
      <img
        src={previewImage}
        alt="Preview"
        className="mt-3"
        style={{ width: '400px', height: '300px', objectFit: 'cover' }}
      />
    )}
  </div>

  <div className="modal-footer ">



    <button
      className="btn btn-primary"
      onClick={handleUpdateTotemImage}
      disabled={isUpdating}
    >
      {isUpdating ? 'Updating...' : 'Update Totem Image'}
    </button>
  </div>
</ReactModal>


            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FamilySettingsEditTotem;
